import { useState, useEffect, useMemo, useCallback, Fragment } from 'react'
import {
  ClipboardList,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  SkipForward,
  Download,
  Loader2,
  FileText,
  RotateCw,
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface AuditEvent {
  id: number
  eventType: 'ingest' | 'generation'
  status: 'success' | 'failed' | 'skipped'
  reason: string | null
  createdAt: string
  applicationId: number | null
  applicationReference: string | null
  clientId: number | null
  clientName: string | null
  letterId: number | null
  errorMessage: string | null
  taskId: string | null
  hasPdf: boolean
}

interface Stats {
  ingest: { success: number; skipped: number; failed: number }
  generation: { success: number; failed: number; skipped: number }
}

interface ClientOption {
  id: number
  companyName: string
}

const PAGE_SIZE = 100

export default function Audit() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<Stats | null>(null)
  const [clients, setClients] = useState<ClientOption[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [pdfModal, setPdfModal] = useState<{ eventId: number; url: string } | null>(null)
  const [retrying, setRetrying] = useState<number | null>(null)

  // Filters
  const [filterClient, setFilterClient] = useState<string>('')
  const [filterEventType, setFilterEventType] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterDateFrom, setFilterDateFrom] = useState<string>('')
  const [filterDateTo, setFilterDateTo] = useState<string>('')

  const buildParams = useCallback(() => {
    const p: Record<string, string | number> = { limit: PAGE_SIZE, offset }
    if (filterClient) p.client_id = filterClient
    if (filterEventType) p.event_type = filterEventType
    if (filterStatus) p.status = filterStatus
    if (filterDateFrom) p.from = new Date(filterDateFrom).toISOString()
    if (filterDateTo) p.to = new Date(filterDateTo + 'T23:59:59').toISOString()
    return p
  }, [offset, filterClient, filterEventType, filterStatus, filterDateFrom, filterDateTo])

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    try {
      const [eventsRes, statsRes] = await Promise.all([
        api.get('/api/v1/audit/', { params: buildParams() }),
        api.get('/api/v1/audit/stats', {
          params: {
            ...(filterDateFrom && { from: new Date(filterDateFrom).toISOString() }),
            ...(filterDateTo && { to: new Date(filterDateTo + 'T23:59:59').toISOString() }),
          },
        }),
      ])
      setEvents(eventsRes.data.events)
      setTotal(eventsRes.data.total)
      setStats(statsRes.data)
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load audit events')
    } finally {
      setLoading(false)
    }
  }, [buildParams, filterDateFrom, filterDateTo])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  useEffect(() => {
    api.get('/api/v1/clients/').then((r) => setClients(r.data)).catch(() => setClients([]))
  }, [])

  const resetFilters = () => {
    setFilterClient('')
    setFilterEventType('')
    setFilterStatus('')
    setFilterDateFrom('')
    setFilterDateTo('')
    setOffset(0)
  }

  const openPdfPreview = async (eventId: number) => {
    try {
      const resp = await api.get(`/api/v1/audit/${eventId}/pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      setPdfModal({ eventId, url })
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load PDF')
    }
  }

  const closePdfPreview = () => {
    if (pdfModal) URL.revokeObjectURL(pdfModal.url)
    setPdfModal(null)
  }

  const retryEvent = async (eventId: number) => {
    if (!confirm('Queue a fresh generation attempt for this application?')) return
    setRetrying(eventId)
    try {
      const resp = await api.post(`/api/v1/audit/${eventId}/retry`)
      toast.success(`Queued — task ${resp.data.task_id.slice(0, 8)}...`)
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Retry failed')
    } finally {
      setRetrying(null)
    }
  }

  const StatusPill = ({ status }: { status: string }) => {
    const cls =
      status === 'success'
        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
        : status === 'failed'
        ? 'bg-red-500/20 text-red-300 border-red-500/30'
        : 'bg-slate-500/20 text-slate-300 border-slate-500/30'
    const Icon = status === 'success' ? CheckCircle : status === 'failed' ? XCircle : SkipForward
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border ${cls}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    )
  }

  const TypePill = ({ type }: { type: string }) => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-slate-700/50 text-slate-300 border border-slate-600/50">
      {type}
    </span>
  )

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1

  const eligibleClients = useMemo(
    () => clients.slice().sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [clients],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-slate-300" />
          <h1 className="text-2xl font-semibold text-slate-100">Audit</h1>
        </div>
        <button onClick={fetchEvents} disabled={loading} className="btn-secondary flex items-center gap-2 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {/* Stats chips */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="card p-3">
            <div className="text-xs text-slate-500 uppercase">Ingested</div>
            <div className="text-2xl font-semibold text-emerald-400">{stats.ingest.success}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-slate-500 uppercase">Ingest dupes</div>
            <div className="text-2xl font-semibold text-slate-400">{stats.ingest.skipped}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-slate-500 uppercase">Ingest failed</div>
            <div className="text-2xl font-semibold text-red-400">{stats.ingest.failed}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-slate-500 uppercase">Letters gen</div>
            <div className="text-2xl font-semibold text-emerald-400">{stats.generation.success}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-slate-500 uppercase">Gen failed</div>
            <div className="text-2xl font-semibold text-red-400">{stats.generation.failed}</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-slate-500 uppercase">Gen skipped</div>
            <div className="text-2xl font-semibold text-slate-400">{stats.generation.skipped}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="label text-xs">Client</label>
            <select value={filterClient} onChange={(e) => { setFilterClient(e.target.value); setOffset(0) }} className="input text-sm">
              <option value="">All clients</option>
              {eligibleClients.map((c) => (
                <option key={c.id} value={c.id}>{c.companyName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">Event type</label>
            <select value={filterEventType} onChange={(e) => { setFilterEventType(e.target.value); setOffset(0) }} className="input text-sm">
              <option value="">Any</option>
              <option value="ingest">Ingest</option>
              <option value="generation">Generation</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Status</label>
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setOffset(0) }} className="input text-sm">
              <option value="">Any</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">From</label>
            <input type="date" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setOffset(0) }} className="input text-sm" />
          </div>
          <div>
            <label className="label text-xs">To</label>
            <input type="date" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setOffset(0) }} className="input text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={resetFilters} className="btn-secondary text-sm w-full">Reset</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="table-header">
              <tr>
                <th className="w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">When</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Application</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Reason</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-500">No events</td>
                </tr>
              )}
              {events.map((ev) => (
                <Fragment key={ev.id}>
                  <tr
                    className={`table-row cursor-pointer ${expandedId === ev.id ? 'bg-slate-800/40' : ''}`}
                    onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)}
                  >
                    <td className="pl-3 text-slate-400">
                      {expandedId === ev.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-400">{new Date(ev.createdAt).toLocaleString('en-GB')}</td>
                    <td className="px-4 py-3"><TypePill type={ev.eventType} /></td>
                    <td className="px-4 py-3 text-sm text-slate-300">{ev.clientName || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-slate-300 max-w-xs truncate">{ev.applicationReference || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3"><StatusPill status={ev.status} /></td>
                    <td className="px-4 py-3 text-sm text-slate-400">{ev.reason || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {ev.hasPdf && (
                        <button onClick={(e) => { e.stopPropagation(); openPdfPreview(ev.id) }} className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-slate-700/50" title="View PDF">
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                      {ev.eventType === 'generation' && ev.status === 'failed' && ev.applicationId && ev.clientId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); retryEvent(ev.id) }}
                          disabled={retrying === ev.id}
                          className="p-1.5 rounded text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50 disabled:opacity-50"
                          title="Retry generation"
                        >
                          {retrying === ev.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === ev.id && (
                    <tr className="bg-slate-800/20">
                      <td colSpan={8} className="px-8 py-3 text-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-slate-500 uppercase mb-1">Event id</div>
                            <div className="text-slate-300">{ev.id}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500 uppercase mb-1">Task id</div>
                            <div className="text-slate-300 font-mono text-xs">{ev.taskId || '—'}</div>
                          </div>
                          {ev.errorMessage && (
                            <div className="col-span-2">
                              <div className="text-xs text-slate-500 uppercase mb-1">Error</div>
                              <div className="text-red-300 font-mono text-xs bg-red-500/10 p-2 rounded border border-red-500/20 whitespace-pre-wrap break-words">{ev.errorMessage}</div>
                            </div>
                          )}
                          {ev.letterId && (
                            <div>
                              <div className="text-xs text-slate-500 uppercase mb-1">Letter id</div>
                              <div className="text-slate-300">{ev.letterId}</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700/50 text-sm">
            <div className="text-slate-500">
              Page {currentPage} of {totalPages} — {total} events
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0} className="btn-secondary text-xs disabled:opacity-50">← Prev</button>
              <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total} className="btn-secondary text-xs disabled:opacity-50">Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {pdfModal && (
        <div className="modal-overlay" onClick={closePdfPreview}>
          <div className="modal-content w-11/12 h-5/6 max-w-6xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
              <h3 className="text-slate-200 font-medium">Application PDF</h3>
              <div className="flex items-center gap-2">
                <a href={pdfModal.url} download className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-slate-700/50" title="Download">
                  <Download className="w-4 h-4" />
                </a>
                <button onClick={closePdfPreview} className="p-1.5 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/50">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe src={pdfModal.url} className="w-full h-full" title="PDF preview" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
